import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, useModel } from '@umijs/max';
import React from 'react';
import { Avatar, Space, Typography } from 'antd';
import {
  AvatarDropdown,
  Footer,
} from '@/components';
import SidebarBrand from '@/components/SidebarBrand';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { assetUrl, clearShareBackgroundAssets, iconPublicUrl, isSharePagePath, syncGlobalBackgroundAssets } from '@/utils/assetUrl';
import { applySiteBrandingToDocument } from '@/utils/siteBranding';
import {
  isAuthExemptPath,
  isLoginPath,
  LOGIN_PATH,
  readStoredUserInfo,
  validateAuthSession,
  hasAuthToken,
} from '@/utils/authSession';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = LOGIN_PATH;
const API_ORIGIN = 'https://api.sc9.cn';

const syncPageBodyClass = (pathname: string) => {
  if (typeof document === 'undefined') return;
  const onShare = isSharePagePath(pathname);
  document.documentElement.classList.toggle('geo-share-page', onShare);
  document.body.classList.toggle('geo-login-page', isLoginPath(pathname));
  document.body.classList.toggle('geo-share-page', onShare);
  if (onShare) {
    clearShareBackgroundAssets();
  } else {
    syncGlobalBackgroundAssets();
  }
};

/** 用户未上传头像时的默认图（public/icons/default_ico.png） */
const defaultUserAvatar = () => assetUrl('icons/default_ico.png');

/** 与 Pro 默认顶栏高度一致（token.layout.header.heightLayoutHeader 默认 56） */
const LAYOUT_HEADER_HEIGHT = 56;

const withApiOrigin = (url?: string) => {
  const val = String(url || '').trim();
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  return `${API_ORIGIN}${val.startsWith('/') ? '' : '/'}${val}`;
};

/** 有头像则走 CDN/API 域名拼接；为空则用本地默认图 */
const resolveUserAvatarSrc = (url?: string) => {
  const val = String(url || '').trim();
  if (!val) return defaultUserAvatar();
  return withApiOrigin(val);
};

const getStoredAvatar = () => {
  const userInfo = readStoredUserInfo<{ avatar?: string }>();
  return String(userInfo?.avatar || '').trim();
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => validateAuthSession();

  const currentUser = await fetchUserInfo();
  
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
}) => {
  return {
    actionsRender: () => [],
    avatarProps: {
      src: resolveUserAvatarSrc((initialState?.currentUser as any)?.avatar || getStoredAvatar()),
      render: (_, avatarChildren) => {
        const user: any = initialState?.currentUser || {};
        const name = user?.company_name || user?.name || user?.username || '用户';
        const avatarSrc = resolveUserAvatarSrc(user?.avatar || getStoredAvatar());
        const userType = Number(user?.user_type || user?.role || 0);
        const roleText =
          userType === 1 ? '超管' :
          userType === 2 ? 'OEM' :
          userType === 3 ? '代理' :
          userType === 4 ? '用户' :
          userType === 5 ? '运营' : '用户';

        return (
          <AvatarDropdown>
            <Space size={10} style={{ cursor: 'pointer' }}>
              <Avatar src={avatarSrc} size={32} />
              <div style={{ lineHeight: 1.2 }}>
                <Typography.Text strong style={{ display: 'block' }}>
                  {name}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {roleText}
                </Typography.Text>
              </div>
            </Space>
          </AvatarDropdown>
        );
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      syncPageBodyClass(location.pathname);
      if (isAuthExemptPath(location.pathname)) {
        return;
      }
      if (!hasAuthToken() || !initialState?.currentUser) {
        history.push(loginPath);
      }
    },
    // 403 页面处理
    unAccessible: <div style={{ textAlign: 'center', padding: '50px' }}>无权访问</div>,
    bgLayoutImgList: [],
    logo: false,
    links: [],
    // 自定义品牌 logo，只在顶部显示图片
    menuHeaderRender: undefined,
    headerTitleRender: (_, __, props) => {
      if (props?.collapsed) {
        return null;
      }
      /** 与侧栏同宽，避免超大 banner 图按固有像素把顶栏 flex 撑爆 */
      const brandW = Number((props as { siderWidth?: number })?.siderWidth) || 215;
      return <SidebarBrand width={brandW} height={LAYOUT_HEADER_HEIGHT} />;
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      const { pathname } = history.location;
      syncPageBodyClass(pathname);
      if (isLoginPath(pathname) || isSharePagePath(pathname)) {
        return <>{children}</>;
      }
      return (
        <div
          className="geo-app-page-bg"
          style={{
            minHeight: '100vh',
            backgroundImage: `url("${iconPublicUrl('web_pic.png')}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          }}
        >
          {children}
        </div>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
/** 首屏渲染前注入背景图路径 */
export function render(oldRender: () => void) {
  if (typeof window !== 'undefined') {
    syncPageBodyClass(window.location.pathname);
    applySiteBrandingToDocument();
  } else {
    syncGlobalBackgroundAssets();
  }
  oldRender();
}

/** 路由切换时同步页面标记（登录页/分享页不加载 web_pic 背景） */
export function onRouteChange({ location }: { location: { pathname: string } }) {
  syncPageBodyClass(location.pathname);
  applySiteBrandingToDocument();
  setTimeout(() => applySiteBrandingToDocument(), 0);
}

export const request: RequestConfig = {
  // 开发环境使用代理，生产环境使用API域名
  baseURL: process.env.NODE_ENV === 'production' ? 'https://api.sc9.cn' : '',
  ...errorConfig,
};
